import dayjs from "dayjs";
import React from "react";
import { Button } from "components/atoms/button";
import { imageSizerString } from "lib/fun";
import { DATE_FORMATS } from "common";

import { Card } from "../../atoms/card";
import { Icon } from "../../atoms/icon";
import { ImageWithLoader } from "../../atoms/imageWithLoader";
import { Tag } from "../../atoms/tag";
import eventCoverPlaceholderSmall from "../../../../assets/images/coverPlaceholderSmall.jpg";
import encourageLogo from "../../../../assets/favicons/mstile-70x70.png";
import logoPlaceholder from "../../../../assets/images/logoPlaceholder.png";
import normalStyles from "./index.module.scss";
import smallStyles from "./small.module.scss";
import { EventInstanceItem, GroupedEventInstanceItem } from "../../../types";

const renderDates = (
  eventInstance: EventInstanceItem | GroupedEventInstanceItem,
) => {
  if (eventInstance.instanceType === "ScheduledEventDate") {
    return (
      <>
        {dayjs(eventInstance.startAt).format(DATE_FORMATS.longWithTime)}
        {` ~ ${dayjs(eventInstance.endAt).format(DATE_FORMATS.longWithTime)}`}
      </>
    );
  }
  if (eventInstance.instanceType === "EntryEvent") {
    return (
      <>{dayjs(eventInstance.deadline).format(DATE_FORMATS.longWithTime)}</>
    );
  }

  // Show range as closest to furthest start dates
  if (eventInstance.instanceType === "GroupedScheduledEventDates") {
    const {
      closest,
      furthest,
    } = eventInstance.selectedScheduledEventDates.reduce(
      (dates, date) => {
        // deadline should be in the future
        if (dayjs().isBefore(date.deadline)) {
          // date is after today and before the closest (new cloesest)
          if (dayjs(date.startAt).isBefore(dates.closest.startAt)) {
            dates.closest = date;
          }
          // date is after today and after the furthest (new furthest)
          if (dayjs(date.startAt).isAfter(dates.furthest.startAt)) {
            dates.furthest = date;
          }
        }
        return dates;
      },
      {
        closest: eventInstance.selectedScheduledEventDates[0],
        furthest: eventInstance.selectedScheduledEventDates[0],
      },
    );

    return (
      <>
        {closest.startAt !== furthest.startAt
          ? `${dayjs(closest.startAt).format(DATE_FORMATS.longWithTime)}` +
            ` ~ ${dayjs(furthest.startAt).format(DATE_FORMATS.longWithTime)}`
          : dayjs(closest.startAt).format(DATE_FORMATS.longWithTime)}
      </>
    );
  }

  return null;
};
interface Props {
  eventInstance: EventInstanceItem | GroupedEventInstanceItem;
  outdated?: boolean;
  openRemovePopup?: (event: React.MouseEvent<HTMLDivElement>) => void;
  openDetailPopup?: (event: React.MouseEvent<HTMLDivElement>) => void;
  mode?: "small" | "default";
  mypage?: boolean;
}

const EventCard: React.FC<Props> = (props) => {
  const {
    eventInstance,
    outdated,
    openRemovePopup,
    openDetailPopup,
    mode = "default",
    mypage,
    ...rest
  } = props;

  const styles = mode === "small" ? smallStyles : normalStyles;

  const event =
    eventInstance.instanceType === "EntryEvent"
      ? eventInstance.event
      : eventInstance.scheduledEvent.event;

  return (
    <Card {...rest} type="Event" smallCard={mode === "small"}>
      <div className={styles.cardContent}>
        <div className={styles.cardHeader}>
          <ImageWithLoader
            className={styles.image}
            fallback={eventCoverPlaceholderSmall}
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            src={`${imageSizerString(event.imageUrl!, 600, 300)}`}
          />

          <div className={styles.graduationYears}>
            {event.targetYears
              .sort((y1, y2) => y1 - y2)
              .map((graduationYear, i) => (
                <div className={styles.graduationYear} key={i}>
                  <Tag shape="round" type="inline" color="black">
                    {graduationYear}
                    年卒
                  </Tag>
                </div>
              ))}
          </div>

          <div className={styles.tag}>
            <Tag color="accent" shape="arrow">
              {eventInstance.instanceType === "ScheduledEventDate" ||
              eventInstance.instanceType === "GroupedScheduledEventDates"
                ? "日程申込"
                : "エントリー"}
            </Tag>
          </div>

          {!outdated && openRemovePopup && (
            <div className={styles.removeEventButton} onClick={openRemovePopup}>
              <Icon
                name="trash"
                className={styles.removeEventButtonIcon}
                size="large"
              />
            </div>
          )}
        </div>

        <div className={styles.cardBody}>
          <div className={styles.dates}>{renderDates(eventInstance)}</div>

          {eventInstance.instanceType === "ScheduledEventDate" && (
            <div className={styles.area}>
              {eventInstance.place.prefecture.name}
            </div>
          )}

          <div className={styles.title}>{event.title}</div>
        </div>

        {event.clients.length > 1 ? (
          <div
            className={styles.clickableCardFooter}
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
            }}
          >
            <ImageWithLoader
              className={styles.companyLogo}
              fallback={eventCoverPlaceholderSmall}
              src={encourageLogo}
            />

            <div className={styles.branches}>
              <span className={styles.branchesTitle}>
                複数企業 ({event.clients.length})
              </span>
              <div className={styles.branchesPopup}>
                {event.clients.map((client, index) => (
                  <p className={styles.companyName} key={index}>
                    {client.company.name}
                  </p>
                ))}
              </div>
            </div>
          </div>
        ) : event.clients.length === 1 ? (
          <div className={styles.cardFooter}>
            <ImageWithLoader
              className={styles.companyLogo}
              fallback={eventCoverPlaceholderSmall}
              src={
                event.clients[0].company.logoImageUrl
                  ? `${imageSizerString(
                      event.clients[0].company.logoImageUrl,
                      36,
                      36,
                    )}`
                  : logoPlaceholder
              }
            />

            <p className={styles.companyName}>
              {event.clients[0].company.name}
            </p>
          </div>
        ) : (
          <div className={styles.cardFooter}>
            <ImageWithLoader
              className={styles.companyLogo}
              fallback={eventCoverPlaceholderSmall}
              src={encourageLogo}
            />

            <p className={styles.companyName}>En-Courage</p>
          </div>
        )}
        {mypage && openDetailPopup && (
          <div className={styles.cardFooter}>
            <div className={styles.buttonContainer} onClick={openDetailPopup}>
              <Button loading={false}>参加情報を見る</Button>
            </div>
          </div>
        )}
      </div>
    </Card>
  );
};

export { EventCard };
